import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Box, Button, Card } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ConfirmDelete from '../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../shared/components/CustomDataGrid';
import { ERole } from '../../../shared/enums/user/ERole';
import { handleError } from '../../../shared/functions/handleError';
import { useHolidayColumns } from '../../../shared/hooks/useHolidayColumns';
import { usePagination } from '../../../shared/hooks/usePagination';
import { IHoliday } from '../../../shared/interfaces/holiday/IHoliday';
import { RootState } from '../../../store/store';
import HolidayFilters from './components/HolidayFilters';
import { AdminHolidaysService } from './service';

const AdminHolidays = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const [data, setData] = useState<IHoliday[] | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState<undefined | IHoliday>(
    undefined,
  );

  const columns = useHolidayColumns(setSelectedHoliday, setShowDeleteModal);
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    setCount,
    changeFilterHandler,
    handleUpdateList,
  } = usePagination({
    perPage: 15,
  });

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedHoliday(undefined);
  };
  const deleteHandler = (id?: number) => {
    !!id &&
      AdminHolidaysService.delete(id)
        .then((response) => {
          if (response) {
            handleUpdateList();
            enqueueSnackbar(
              t('toasts.successfullyDeleted', { title: selectedHoliday?.name }),
              {
                variant: 'success',
              },
            );
            closeDeleteModal();
          }
        })
        .catch((err) => handleError(err));
  };

  useEffect(() => {
    AdminHolidaysService.get({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch((err) => handleError(err));
  }, [pagination, setTotalPages, setCount]);
  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingBottom: '20px',
        }}
      >
        <HolidayFilters
          pagination={pagination}
          setPagination={setPagination}
          changeFilterHandler={changeFilterHandler}
        />
        {!isNormalUser && (
          <Button
            sx={{ ml: '10px' }}
            variant={'contained'}
            onClick={() => navigate('/admin/holiday/create')}
          >
            <AddCircleOutlineOutlinedIcon sx={{ mr: '5px' }} />
            {t('button.addNewHoliday')}
          </Button>
        )}
      </Box>

      <CustomDataGrid
        columns={columns}
        rows={data || []}
        rowCount={count}
        setPagination={setPagination}
        pagination={pagination}
        loading={data === undefined}
        hasNextPage={totalPages < pagination?.page}
      />

      <ConfirmDelete
        open={showDeleteModal}
        title={selectedHoliday?.name}
        handleClose={closeDeleteModal}
        handleSubmit={() => deleteHandler(selectedHoliday?.id)}
      />
    </Card>
  );
};

export default AdminHolidays;
